import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, RefreshCw, Users, CreditCard, UserX } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const AdminPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [paidUsers, setPaidUsers] = useState([]);
  const [freeUsers, setFreeUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [summaryRes, paidRes, freeRes] = await Promise.all([
        api.get('/admin/summary'),
        api.get('/admin/users-list?type=paid'),
        api.get('/admin/users-list?type=free')
      ]);
      
      setSummary(summaryRes.data);
      setPaidUsers(paidRes.data);
      setFreeUsers(freeRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setError('Brak uprawnień');
      } else {
        setError('Błąd pobierania danych');
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'transparent' }}>
        <Card style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }} className="border-0">
          <CardContent className="py-8 px-12 text-center">
            <p className="text-xl text-red-400 font-semibold">{error}</p>
            <Button onClick={() => navigate('/profile')} className="mt-4" variant="outline">
              Wróć
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" style={{ background: 'transparent' }}>
      {/* Header */}
      <div className="px-6 pt-8 pb-4">
        <button onClick={() => navigate('/profile')} className="mb-4 text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Panel administratora</h1>
          <Button 
            onClick={fetchData} 
            disabled={loading}
            variant="outline" 
            size="sm"
            className="border-slate-600 text-slate-300 hover:text-white hover:border-slate-400"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Odśwież
          </Button>
        </div>
        <p className="text-sm text-slate-500 mt-1">Auto-odświeżanie co 30s</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="px-6 mb-6">
          <div className="grid grid-cols-3 gap-3">
            <Card style={{ background: 'rgba(14, 165, 233, 0.15)', backdropFilter: 'blur(8px)' }} className="border-0">
              <CardContent className="py-4 text-center">
                <Users className="w-5 h-5 text-sky-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{summary.totalUsers}</p>
                <p className="text-xs text-slate-400">Wszyscy</p>
              </CardContent>
            </Card>
            <Card style={{ background: 'rgba(16, 185, 129, 0.15)', backdropFilter: 'blur(8px)' }} className="border-0">
              <CardContent className="py-4 text-center">
                <CreditCard className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{summary.paidActive}</p>
                <p className="text-xs text-slate-400">Płacący</p>
              </CardContent>
            </Card>
            <Card style={{ background: 'rgba(148, 163, 184, 0.15)', backdropFilter: 'blur(8px)' }} className="border-0">
              <CardContent className="py-4 text-center">
                <UserX className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{summary.freeUsers}</p>
                <p className="text-xs text-slate-400">Darmowi</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Paid Users Table */}
      <div className="px-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-emerald-400" />
          Płacący ({paidUsers.length})
        </h2>
        <Card style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(8px)' }} className="border-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Plan</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {paidUsers.length === 0 ? (
                  <tr><td colSpan="3" className="py-4 px-4 text-center text-slate-500">Brak płacących użytkowników</td></tr>
                ) : (
                  paidUsers.map((user, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50 hover:bg-white/5">
                      <td className="py-3 px-4 text-white">{user.email}</td>
                      <td className="py-3 px-4 text-emerald-400">{user.plan || '-'}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
                          {user.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Free Users Table */}
      <div className="px-6">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <UserX className="w-5 h-5 text-slate-400" />
          Darmowi ({freeUsers.length})
        </h2>
        <Card style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(8px)' }} className="border-0 overflow-hidden">
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0" style={{ background: 'rgba(15, 23, 42, 0.95)' }}>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {freeUsers.length === 0 ? (
                  <tr><td colSpan="2" className="py-4 px-4 text-center text-slate-500">Brak darmowych użytkowników</td></tr>
                ) : (
                  freeUsers.map((user, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50 hover:bg-white/5">
                      <td className="py-3 px-4 text-slate-300">{user.email}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 rounded-full text-xs bg-slate-500/20 text-slate-400">
                          {user.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};
